import type { SectionProps } from "../../../components/Questionnaire/Section";
import {
  isQuestion,
  type QuestionProps,
  type QuestionOrGroup,
} from "../../../components/Questionnaire/Section/Question/types";
import type { RuleSet } from "../../../components/Questionnaire/validation/form/types";
import type { QuestionnaireState } from "../types";
import { questionnaireInitialState } from "../reducer/initialState";
import { handleSetAction } from "../reducer/actions";
import { handleSetConfigAction } from "../reducer/actions";
import { handleSetValidationsAction } from "../reducer/actions";

const isPersonalisedCover = (q: QuestionOrGroup): q is QuestionProps =>
  isQuestion(q) && q.display?.type === "personalised_cover";

/**
 * Hide non-suggested personalised covers and flag whether "see more" is needed.
 * Returns state unchanged if all covers are suggested or none are.
 */
export const applyCoverGeniusVisibility = (
  state: QuestionnaireState,
): {
  state: QuestionnaireState;
  seeMoreCovers: boolean;
  hiddenCoverNames: Set<string>;
} => {
  const allQuestions = state.sections.flatMap(s => s.questions);

  const visibleCovers = allQuestions
    .filter(isPersonalisedCover)
    .filter(q => state.visibility?.[q.name]);

  // Partition into suggested and non-suggested in a single pass
  const suggestedCovers: QuestionProps[] = [];
  const unsuggestedCovers: QuestionProps[] = [];
  for (const cover of visibleCovers) {
    if (isQuestion(cover) && cover.suggested) {
      suggestedCovers.push(cover);
    } else {
      unsuggestedCovers.push(cover);
    }
  }

  const allCoversAreSuggested =
    suggestedCovers.length === 0 || unsuggestedCovers.length === 0;

  if (allCoversAreSuggested) {
    return { state, seeMoreCovers: false, hiddenCoverNames: new Set() };
  }

  // Hide non-suggested covers
  const hiddenCoverNames = new Set(unsuggestedCovers.map(q => q.name));
  const visibilityWithHiddenCovers = { ...state.visibility };
  for (const name of hiddenCoverNames) {
    visibilityWithHiddenCovers[name] = false;
  }

  // Stamp question.visible to match the visibility map so the
  // props.visible fallback in Question renders correctly before hydration.
  const sectionsWithHiddenCovers = state.sections.map(section => ({
    ...section,
    questions: section.questions.map(question => {
      if (isQuestion(question) && hiddenCoverNames.has(question.name)) {
        return { ...question, visible: false };
      }
      return question;
    }),
  }));

  return {
    state: {
      ...state,
      visibility: visibilityWithHiddenCovers,
      sections: sectionsWithHiddenCovers,
    },
    seeMoreCovers: true,
    hiddenCoverNames,
  };
};

/**
 * Compute fully-transformed questionnaire state synchronously for hydration.
 * Replays: setConfig → setSections → setValidations → applyCoverGeniusVisibility.
 */
export const computeInitialQuestionnaireState = (
  sections?: SectionProps[],
  validations?: readonly RuleSet[],
  config: Record<string, unknown> = {},
): {
  state: QuestionnaireState;
  seeMoreCovers: boolean;
  hiddenCoverNames: Set<string>;
} => {
  let state: QuestionnaireState = questionnaireInitialState;

  const hasConfig = Object.keys(config).length > 0;
  if (hasConfig) {
    state = handleSetConfigAction(state, config);
  }

  const hasSections = !!sections;
  if (hasSections) {
    state = handleSetAction(state, sections);
  }

  const hasValidations = !!validations && validations.length > 0;
  if (hasValidations) {
    state = handleSetValidationsAction(state, validations);
  }

  return applyCoverGeniusVisibility(state);
};
